import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Slate, Editable, withReact, useSlate, useFocused } from "slate-react";
import ReactDOM from "react-dom";
import { createEditor, Editor, Transforms, Range, Node, Text } from "slate";
import { withHistory } from "slate-history";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled from "styled-components";
import { shortTimecode } from "../TimeCodeConverter";
import UpdateSpeaker from "./UpdateSpeaker";
import { secondsToTimecode } from "../TimeCodeConverter/secondsToTimecodeNew";
import { isArray } from "lodash";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
} from "@material-ui/core";
import "./modal.scss";
import { CustomInputField } from "../../common/FormInputs";
import {
  SpeakerBadge,
  TextContent,
  TimecodeChip,
  TranscriptWrapper,
  TranscriptContent,
  TranscriptBlock,
  MetadataBar,
} from "./Rawtranscript";
import {
  Delete,
  HighlightOff,
  FormatColorFill,
  FileCopy,
  Close,
  ChatBubbleOutline,
  ThumbUp,
  ThumbDown,
} from "@material-ui/icons";
import useUser from "../../../hooks/useUser";
import moment from "moment";

const useStyles = makeStyles({
  table: {
    backgroundColor: "transparent",
    borderRadius: 7,
    "& .MuiTableCell-root": {
      borderBottom: 0,
    },
  },
  tableHead: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.TRANSCRIPT_GRAY,
    fontSize: 10,
    paddingBottom: 0,
  },
  row: {
    borderBottom: `1px solid ${COLORS.INPUT_BORDER}`,
  },
  formLabel: {
    fontFamily: theme.fonts.primaryFontRegular,
    color: COLORS.COLOR_DARK,
    marginRight: 5,
    // minWidth: 98,
  },
});

const getInitials = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const initials =
    parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : trimmed.slice(0, 2);
  return initials.toUpperCase();
};

const extractCommentsFromEditorValue = (nodes) => {
  const commentsById = new Map();

  const visit = (node) => {
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!node || typeof node !== "object") return;

    if (Array.isArray(node.children)) {
      node.children.forEach(visit);
      return;
    }

    if (node.commentId) {
      const existing = commentsById.get(node.commentId);
      if (!existing) {
        commentsById.set(node.commentId, {
          id: node.commentId,
          quotedText: node.commentQuotedText || node.text || "",
          commentText: node.commentText || "",
          author: node.commentAuthor || "Unknown",
          status: node.commentStatus || "pending",
          createdAt: node.commentCreatedAt || null,
          resolvedAt: node.commentResolvedAt || null,
        });
      } else if (!existing.quotedText && node.text) {
        existing.quotedText = node.text;
      }
    }
  };

  visit(nodes);

  return Array.from(commentsById.values()).sort((a, b) =>
    String(a.createdAt || "").localeCompare(String(b.createdAt || "")),
  );
};

const clampPopupPosition = (top, left, width = 288, height = 0) => {
  const margin = 12;
  const viewportLeft = window.scrollX + margin;
  const viewportRight = window.scrollX + window.innerWidth - width - margin;
  const clampedLeft = Math.min(
    Math.max(left, viewportLeft),
    Math.max(viewportLeft, viewportRight),
  );

  let clampedTop = top;
  if (height) {
    const viewportBottom =
      window.scrollY + window.innerHeight - height - margin;
    clampedTop = Math.min(
      top,
      Math.max(window.scrollY + margin, viewportBottom),
    );
  }

  return { top: clampedTop, left: clampedLeft };
};

const Index = ({
  data,
  setData,
  handleWordClick,
  currentTime,
  showTimecodes,
  isEditable,
  showSpeakers,
  speakersData,
  timecodeOffset,
  updateState,
  render,
  setRender,
  captureText,
  name,
  role,
  isPlaying,
  setFilterSpeaker,
  LiveTranscription,
  setIsEdited,
  state,
  setState,
  genericLive,
  interimResults,
  endTranscript,
}) => {
  const editor = useMemo(() => withReact(withHistory(createEditor())), []);
  const [value, setValue] = useState([]);
  const [trigger, setTrigger] = useState({});
  const [cursor, setCursor] = useState({});
  const [search, setSearch] = useState("");
  const [replace, setReplace] = useState("");
  const [modal, setModal] = useState(false);
  const classes = useStyles();
  const containerRef = useRef(null);
  const [commentComposer, setCommentComposer] = useState({
    open: false,
    top: 0,
    left: 0,
    range: null,
  });
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [activeCommentPos, setActiveCommentPos] = useState({ top: 0, left: 0 });
  const [commentDraft, setCommentDraft] = useState("");
  const { userObject } = useUser();

  const comments = useMemo(
    () => extractCommentsFromEditorValue(editor.children),
    [editor.children],
  );

  const canApproveComments = true;

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 20);
    }
  }, []);

  const openModal = (e) => {
    if (e.ctrlKey && e.key === "h") {
      e.preventDefault();
      setModal(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", openModal);
    return () => {
      window.removeEventListener("keydown", openModal);
    };
  }, []);

  const handleReplaceAll = () => {
    if (!search || !replace) {
      alert("Please enter both search and replace terms.");
      return;
    }

    // Traverse all text nodes in the editor
    for (const [node, path] of Node.texts(editor)) {
      if (Text.isText(node) && node.text.includes(search)) {
        const newText = node.text.split(search).join(replace);
        // Only replace the text in the node without modifying the structure
        Transforms.insertText(editor, newText, { at: path });
      }
    }

    setModal(false);
    setSearch("");
    setReplace("");
    editor.selection = null;
  };

  const replaceTextOneByOne = (targetText, newText) => {
    // Get the editor's content as nodes
    const { selection } = editor;

    if (!selection) return; // Ensure there's a selection

    // Iterate through the editor nodes
    for (const [node, path] of Editor.nodes(editor, {
      at: [],
      match: Text.isText, // Match text nodes only
    })) {
      const { text } = node;

      // Find the position of the target text
      const index = text.indexOf(targetText);
      if (index !== -1) {
        // Move the selection to the target text
        Transforms.select(editor, {
          anchor: { path, offset: index },
          focus: { path, offset: index + targetText.length },
        });

        // Replace the selected text with the new text
        Transforms.insertText(editor, newText);

        // Exit the function after the first replacement
        return;
      }
    }
  };

  const getMediaCurrentTime = secondsToTimecode(currentTime + timecodeOffset);

  // Function to track whether changes have been made
  const detectChanges = () => {
    // Check if the history of operations has any entries
    if (LiveTranscription) {
      if (editor.history.redos.length > 0 || editor.history.undos.length > 0) {
        setIsEdited(true);
      } else {
        setIsEdited(false);
      }
    }
  };

  // Detect changes using onChange by checking the editor's history
  useEffect(() => {
    detectChanges(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor.history]);

  useEffect(() => {
    const callback = (event) => {
      if (event.code === "Backquote") {
        navigator.clipboard.writeText(getMediaCurrentTime);
      }
    };
    document.addEventListener("keydown", callback);
    return () => {
      document.removeEventListener("keydown", callback);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime]);

  useEffect(() => {
    if (data) {
      const response = splitContent(data, currentTime);
      setValue(response);
      requestAnimationFrame(scrollToBottom);
      setRender(false);
    }
    if (value) {
      editor.history.undos = [];
      editor.children = value;
      editor.selection = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [render]);

  useEffect(() => {
    if (value) {
      setData(
        value.map((el) => {
          return {
            id: el?.id,
            end: el?.end,
            confidence: el?.confidence,
            text: el?.children,
            start: el?.start,
            speaker: el?.speaker,
            mediaId: el?.mediaId,
            transcriptionFlag: el?.transcriptionFlag,
            speechFinal: el?.speechFinal,
            utteranceEnd: el?.utteranceEnd,
            lastTranscriptEndTime: el?.lastTranscriptEndTime,
          };
        }),
      );
      if (LiveTranscription) {
        let result = {};
        value.map((item) => {
          return (
            item?.oldSpeaker !== item?.speaker &&
            (result[item?.oldSpeaker] = item?.speaker)
          );
        });
        setFilterSpeaker(result);
      }
      if (genericLive) {
        let result = {};
        value.map((item) => {
          return (
            item?.oldSpeaker !== item?.speaker &&
            (result[item?.oldSpeaker] = item?.speaker)
          );
        });
        setFilterSpeaker(result);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function generatePreviousTimingsUpToCurrent(start, end) {
    const startTimeInt = parseInt(start);
    const endTimeInt = parseInt(end);
    return `${startTimeInt} ${endTimeInt}`;
  }

  const renderElement = useCallback((props) => {
    switch (props.element.type) {
      case "timedText":
        return <TimedTextElement {...props} />;
      default:
        return <DefaultElement {...{ props }} />;
    }
  }, []);

  const DefaultElement = (props) => {
    return <p {...props.attributes}>{props.children}</p>;
  };

  const TimedTextElement = (props) => {
    let startTimecode = props.element?.start;
    if (timecodeOffset) {
      startTimecode += timecodeOffset;
    }

    return (
      <TranscriptBlock>
        <MetadataBar contentEditable={false}>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {showSpeakers && (
              <SpeakerBadge
                style={{ cursor: "pointer" }}
                onClick={() =>
                  editor.children[editor.children.length - 1].children[0]
                    .text === props.element.children[0].text
                    ? ""
                    : setState({
                        state: true,
                        element: props.element,
                      })
                }
              >
                {props.element.speaker}
              </SpeakerBadge>
            )}
            {showTimecodes ? (
              <TimecodeChip
                style={{ cursor: "pointer" }}
                data-start={props.element.start}
                onClick={() => handleWordClick(startTimecode)}
              >
                {shortTimecode(startTimecode)}
              </TimecodeChip>
            ) : null}{" "}
            {props.element?.confidence ? (
              <ValueConfidence>{`${props.element?.confidence} %`}</ValueConfidence>
            ) : null}
          </div>
          <div
            style={{
              fontSize: "11px",
              fontFamily: theme.fonts.primaryFontSemiBold,
              color: "#64748b",
            }}
          >
            {props.element?.id}
          </div>
        </MetadataBar>
        <TextContent
          style={{
            color: COLORS.COLOR_DARK,
          }}
          onDoubleClick={() => handleWordClick(startTimecode)}
        >
          {props.children}
        </TextContent>
      </TranscriptBlock>
    );
  };

  const generateText = (words) => {
    return words.text;
  };

  const splitContent = (paragraphs) => {
    return paragraphs.map((paragraph, index) => ({
      speaker: paragraph.speaker,
      confidence: paragraph.confidence,
      id: paragraph.id,
      start: paragraph?.start,
      end: paragraph?.end,
      previousTimings: generatePreviousTimingsUpToCurrent(
        paragraph.start,
        paragraph.end,
      ),
      oldSpeaker: paragraph?.oldSpeaker
        ? paragraph?.oldSpeaker
        : paragraph?.speaker,
      mediaId: paragraph?.mediaId,
      transcriptionFlag: paragraph?.transcriptionFlag,
      speechFinal: paragraph?.speechFinal,
      utteranceEnd: paragraph?.utteranceEnd,
      lastTranscriptEndTime: paragraph?.lastTranscriptEndTime,
      // pre-computing the display of the formatting here so that it doesn't need to convert it in leaf render
      startTimecode: paragraph.start,
      type: "timedText",
      children: isArray(paragraph.text)
        ? paragraph.text
        : [
            {
              text: generateText(paragraph),
            },
          ],
      // children: [
      //   {
      //     text: generateText(paragraph),
      //   },
      // ],
    }));
  };

  /*
  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf["suggestionText"] || leaf["name"]) {
      return (
        <>
          <Tooltip
            title={
              leaf["name"]
                ? `${
                    leaf["suggestionText"]
                      ? leaf["suggestionText"]
                      : leaf["role"]
                  }  - ${leaf["name"]}`
                : leaf["suggestionText"]
            }
            placement="top"
          >
            <span
              className={"timecode text"}
              style={{
                borderRadius: "5px",
                backgroundColor: leaf["color"],
              }}
              data-start={children.props.parent.start}
              data-previous-timings={children.props.parent.previousTimings}
              {...attributes}
            >
              {children}{" "}
            </span>
          </Tooltip>
          {leaf["suggestionText"] && (
            <>
              <ActionButton
                contentEditable={false}
                onClick={() =>
                  handleChange(children.props.leaf, children.props.parent)
                }
              >
                <img
                  style={{ margin: "-4px" }}
                  src={require("../../../assets/images/checkBoxSelected.svg")}
                  alt="submit"
                />
              </ActionButton>
              <ActionButton
                style={{ marginLeft: "2px" }}
                contentEditable={false}
                onClick={() =>
                  handleRemove(children.props.leaf, children.props.parent)
                }
              >
                <img
                  style={{ margin: "-4px" }}
                  src={require("../../../assets/images/closeButton.svg")}
                  alt="submit"
                />
              </ActionButton>
            </>
          )}
        </>
      );
    }
    return (
      <span
        className={"timecode text"}
        style={{
          borderRadius: "5px",
          backgroundColor: leaf["color"],
        }}
        data-start={children.props.parent.start}
        data-previous-timings={children.props.parent.previousTimings}
        {...attributes}
      >
        {children}
      </span>
    ); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  */

  const toggleSelectedTextHighlight = useCallback(() => {
    if (
      !editor.selection ||
      Editor.string(editor, editor.selection).trim() === ""
    ) {
      return;
    }

    if (Editor.marks(editor)?.highlight) {
      Editor.removeMark(editor, "highlight");
      Editor.removeMark(editor, "highlightAuthor");
    } else {
      Editor.addMark(editor, "highlight", true);
      Editor.addMark(editor, "highlightAuthor", userObject?.name || "Viewer");
    }

    Transforms.deselect(editor);
    window.getSelection().removeAllRanges(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, editor, userObject?.name]);

  const removeHighlightFromSelection = useCallback(() => {
    if (
      !editor.selection ||
      Editor.string(editor, editor.selection).trim() === ""
    ) {
      return;
    }
    Editor.removeMark(editor, "highlight");
    Editor.removeMark(editor, "highlightAuthor");
    Transforms.deselect(editor);
    window.getSelection().removeAllRanges(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments, editor]);

  const generateCommentId = () =>
    `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const openCommentComposer = useCallback(() => {
    if (
      !editor.selection ||
      Editor.string(editor, editor.selection).trim() === ""
    ) {
      return;
    }
    const domSelection = window.getSelection();
    if (!domSelection || domSelection.rangeCount === 0) return;
    const domRange = domSelection.getRangeAt(0);
    const rect = domRange.getBoundingClientRect();
    const { top, left } = clampPopupPosition(
      rect.bottom + window.scrollY + 10,
      rect.left + window.scrollX,
      300,
    );
    setCommentComposer({
      open: true,
      top,
      left,
      range: editor.selection,
    });
    setCommentDraft("");
  }, [editor]);

  const handleCopyText = useCallback(() => {
    const selection = editor.selection;
    const text = selection ? Editor.string(editor, selection).trim() : "";
    if (!text) return;

    const finishCopy = () => {};
    const fallbackCopy = () => {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (copied) finishCopy();
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(finishCopy).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }

    Transforms.deselect(editor);
    window.getSelection().removeAllRanges();
  }, [editor]);

  const closeCommentComposer = useCallback(() => {
    setCommentComposer({ open: false, top: 0, left: 0, range: null });
    setCommentDraft("");
  }, []);

  const submitComment = useCallback(() => {
    const text = commentDraft.trim();
    if (!text || !commentComposer.range) return;

    const id = generateCommentId();
    const quotedText = Editor.string(editor, commentComposer.range);
    const createdAt = new Date().toISOString();
    const author = userObject?.name || "You";

    Transforms.select(editor, commentComposer.range);
    Editor.addMark(editor, "commentId", id);
    Editor.addMark(editor, "commentStatus", "pending");
    Editor.addMark(editor, "commentText", text);
    Editor.addMark(editor, "commentAuthor", author);
    Editor.addMark(editor, "commentCreatedAt", createdAt);
    Editor.addMark(editor, "commentQuotedText", quotedText);

    setCommentComposer({ open: false, top: 0, left: 0, range: null });
    setCommentDraft("");
    Transforms.deselect(editor);
    window.getSelection().removeAllRanges();
  }, [commentComposer, commentDraft, editor, userObject]);

  const resolveComment = useCallback(
    (commentId, status) => {
      if (!canApproveComments) return;

      const matches = Array.from(
        Editor.nodes(editor, {
          at: [],
          match: (n) => n.commentId === commentId,
        }),
      );

      if (matches.length > 0) {
        const firstNode = matches[0][0];
        const commentText = firstNode.commentText || "";

        const firstPath = matches[0][1];
        const lastMatch = matches[matches.length - 1];
        const lastPath = lastMatch[1];
        const lastNode = lastMatch[0];

        const range = {
          anchor: { path: firstPath, offset: 0 },
          focus: {
            path: lastPath,
            offset: lastNode.text ? lastNode.text.length : 0,
          },
        };

        if (status === "approved") {
          Transforms.insertText(editor, commentText, { at: range });

          for (const [, path] of Editor.nodes(editor, {
            at: [],
            match: (n) => n.commentId === commentId,
          })) {
            Transforms.unsetNodes(
              editor,
              [
                "commentId",
                "commentStatus",
                "commentText",
                "commentAuthor",
                "commentCreatedAt",
                "commentQuotedText",
                "commentResolvedAt",
              ],
              { at: path },
            );
          }
        } else if (status === "rejected") {
          for (const [, path] of Editor.nodes(editor, {
            at: [],
            match: (n) => n.commentId === commentId,
          })) {
            Transforms.unsetNodes(
              editor,
              [
                "commentId",
                "commentStatus",
                "commentText",
                "commentAuthor",
                "commentCreatedAt",
                "commentQuotedText",
                "commentResolvedAt",
              ],
              { at: path },
            );
          }
        }
      }

      setActiveCommentId(null);
    },
    [editor, canApproveComments],
  );

  const deleteComment = useCallback(
    (commentId) => {
      if (!canApproveComments) return;

      for (const [, path] of Editor.nodes(editor, {
        at: [],
        match: (n) => n.commentId === commentId,
      })) {
        Transforms.unsetNodes(
          editor,
          [
            "commentId",
            "commentStatus",
            "commentText",
            "commentAuthor",
            "commentCreatedAt",
            "commentQuotedText",
            "commentResolvedAt",
          ],
          { at: path },
        );
      }

      setActiveCommentId(null);
    },
    [editor, canApproveComments],
  );

  const openCommentThread = useCallback((commentId, anchorEl) => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const { top, left } = clampPopupPosition(
      rect.bottom + window.scrollY + 10,
      rect.left + window.scrollX,
      300,
    );
    setActiveCommentPos({ top, left });
    setActiveCommentId((prev) => (prev === commentId ? null : commentId));
  }, []);

  const activeComment = useMemo(
    () => comments.find((c) => c.id === activeCommentId) || null,
    [comments, activeCommentId],
  );

  const HoveringToolbar = () => {
    const ref = useRef(null);
    const editor = useSlate();
    const inFocus = useFocused();

    useEffect(() => {
      const el = ref.current;
      const { selection } = editor;

      if (!el) return;

      if (
        !selection ||
        !inFocus ||
        Range.isCollapsed(selection) ||
        Editor.string(editor, selection).trim() === ""
      ) {
        el.style.opacity = "0";
        el.style.pointerEvents = "none";
        el.style.top = "-10000px";
        el.style.left = "-10000px";
        return;
      }

      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) return;
      const domRange = domSelection.getRangeAt(0);
      const rect = domRange.getBoundingClientRect();

      el.style.opacity = "1";
      el.style.pointerEvents = "auto";
      el.style.top = `${rect.top + window.scrollY - el.offsetHeight - 6}px`;
      el.style.left = `${
        rect.left + window.scrollX - el.offsetWidth / 2 + rect.width / 2
      }px`;
    });

    const isHighlighted = Editor.marks(editor)?.highlight;

    return ReactDOM.createPortal(
      <FloatingHighlightBar
        ref={ref}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        <FloatingToolbarBtn
          onClick={toggleSelectedTextHighlight}
          title={isHighlighted ? "Remove highlight" : "Highlight selected text"}
        >
          <FormatColorFill style={{ fontSize: 18 }} />
        </FloatingToolbarBtn>

        {isHighlighted && (
          <FloatingToolbarBtn
            onClick={removeHighlightFromSelection}
            title="Remove highlight"
          >
            <HighlightOff style={{ fontSize: 18 }} />
          </FloatingToolbarBtn>
        )}

        <FloatingToolbarBtn onClick={openCommentComposer} title="Add comment">
          <ChatBubbleOutline style={{ fontSize: 18 }} />
        </FloatingToolbarBtn>

        <FloatingToolbarBtn onClick={handleCopyText} title="Copy selected text">
          <FileCopy style={{ fontSize: 18 }} />
        </FloatingToolbarBtn>
      </FloatingHighlightBar>,
      document.body,
    );
  };

  const renderLeaf = useCallback(
    ({ attributes, children, leaf }) => {
      if (leaf.commentId) {
        return (
          <CommentedText
            {...attributes}
            $status={leaf.commentStatus}
            onClick={(e) => {
              e.stopPropagation();
              openCommentThread(leaf.commentId, e.currentTarget);
            }}
          >
            {children}
          </CommentedText>
        );
      }

      const backgroundColor = leaf.activeSearchMatch
        ? "#f59e0b"
        : leaf.searchMatch
          ? "#fef08a"
          : leaf.highlight
            ? "#fde68a"
            : "transparent";

      return (
        <span
          {...attributes}
          title={
            leaf.highlightAuthor
              ? `Highlighted by ${leaf.highlightAuthor}`
              : undefined
          }
          style={{
            backgroundColor,
            borderRadius: backgroundColor === "transparent" ? 0 : "2px",
            color: leaf.activeSearchMatch ? "#1f2937" : "inherit",
          }}
        >
          {children}
        </span>
      );
    },
    [openCommentThread],
  );

  // const handleOnKeyDown = (event) => {
  //   if (event.altKey && event.key === "s") {
  //     updateState(data);
  //   } else if (event.key === "Backspace") {
  //     const { selection } = editor;
  //     const { focus } = selection;
  //     const { offset } = focus;
  //     if (focus && offset === 0 && focus.path.length > 1) {
  //       const prevPath = focus.path.slice(0, focus.path.length - 1);
  //       const prevNode = Editor.node(editor, prevPath)[0];
  //       const addNode = Editor.node(editor, [prevPath - 1])[0];
  //       Transforms.setNodes(
  //         editor,
  //         {
  //           type: "timedText",
  //           mediaId: addNode?.mediaId + "," + prevNode?.mediaId,
  //           end: prevNode?.end,
  //         },
  //         { at: [prevPath - 1] }
  //       );
  //       // const firstBlock = Editor.node(editor, [0])[1];
  //       // if (!firstBlock) {
  //       //   const prevPath = focus.path.slice(0, focus.path.length - 1);
  //       //   const prevNode = Editor.node(editor, prevPath)[0];
  //       //   const addNode = Editor.node(editor, [prevPath - 1])[0];
  //       //   Transforms.setNodes(
  //       //     editor,
  //       //     {
  //       //       type: "timedText",
  //       //       mediaId: addNode?.mediaId + "," + prevNode?.mediaId,
  //       //       end: prevNode?.end,
  //       //     },
  //       //     { at: [prevPath - 1] }
  //       //   );
  //       // }
  //     }
  //   } else if (event.key === "Delete") {
  //     const { selection } = editor;
  //     const { focus } = selection;
  //     const { offset } = focus;
  //     const prevPath = focus.path.slice(0, focus.path.length - 1);
  //     const prevNode = Editor.node(editor, prevPath)[0];
  //     if (prevNode.children[0].text.length === offset) {
  //       const addNode = Editor.node(editor, [parseInt(prevPath) + 1])[0];
  //       Transforms.setNodes(
  //         editor,
  //         {
  //           type: "timedText",
  //           mediaId: prevNode?.mediaId + "," + addNode?.mediaId,
  //           end: prevNode?.end,
  //         },
  //         { at: [prevPath] }
  //       );
  //       // if (![parseInt(editor.children.length - 1)]) {
  //       //   const addNode = Editor.node(editor, [parseInt(prevPath) + 1])[0];
  //       //   Transforms.setNodes(
  //       //     editor,
  //       //     {
  //       //       type: "timedText",
  //       //       mediaId: prevNode?.mediaId + "," + addNode?.mediaId,
  //       //       end: prevNode?.end,
  //       //     },
  //       //     { at: [prevPath] }
  //       //   );
  //       // }
  //     }
  //   }
  // };

  // const handleOnKeyDown = (event) => {
  //   if (event.altKey && event.key === "s") {
  //     updateState(data);
  //   } else if (event.key === "Backspace") {
  //     const { selection } = editor;
  //     const { focus } = selection;
  //     const { offset } = focus;
  //     if (focus && offset === 0 && focus.path.length > 1) {
  //       const prevPath = focus.path.slice(0, focus.path.length - 1);
  //       const prevNode = Editor.node(editor, prevPath)[0];
  //       const addNode = Editor.node(editor, [prevPath - 1])[0];
  //       Transforms.setNodes(
  //         editor,
  //         {
  //           type: "timedText",
  //           mediaId: addNode?.mediaId + "," + prevNode?.mediaId,
  //           end: prevNode?.end,
  //         },
  //         { at: [prevPath - 1] }
  //       );
  //     } else {
  //       if (selection && Range.isExpanded(selection)) {
  //         const prevPath = focus.path.slice(0, focus.path.length - 1);
  //         Transforms.setNodes(editor, { at: [prevPath] });
  //       }
  //     }
  //   } else if (event.key === "Delete") {
  //     const { selection } = editor;
  //     const { focus } = selection;
  //     const { offset } = focus;
  //     const prevPath = focus.path.slice(0, focus.path.length - 1);
  //     const prevNode = Editor.node(editor, prevPath)[0];
  //     if (prevNode.children[0].text.length === offset) {
  //       const addNode = Editor.node(editor, [parseInt(prevPath) + 1])[0];
  //       const [start] = Range.edges(selection);
  //       const pointAfter = Editor.after(editor, start);
  //       const blockAfter = Editor.node(editor, [pointAfter.path[0]])[0];
  //       Transforms.setNodes(
  //         editor,
  //         {
  //           type: "timedText",
  //           mediaId: prevNode?.mediaId + "," + addNode?.mediaId,
  //           end: blockAfter?.end,
  //         },
  //         { at: [prevPath] }
  //       );
  //     } else {
  //       if (selection && Range.isExpanded(selection)) {
  //         const prevPath = focus.path.slice(0, focus.path.length - 1);
  //         Transforms.setNodes(editor, { at: [prevPath] });
  //       }
  //     }
  //   } else if (event.key === "Enter") {
  //     const { selection } = editor;
  //     const { focus } = selection;
  //     if (selection && Range.isExpanded(selection)) {
  //       const prevPath = focus.path.slice(0, focus.path.length - 1);
  //       Transforms.setNodes(editor, { at: [prevPath] });
  //     } else {
  //       const [start] = Range.edges(selection);

  //       const pointBefore = Editor.before(editor, start);
  //       const nodeBefore = Editor.node(editor, pointBefore)[0];
  //       const splitTxet = nodeBefore.text.split(" ");
  //       const textLength = splitTxet.length;
  //       const nextPath = [pointBefore.path[0] + 1];
  //       const timeStampCheck = [pointBefore.path[0] + 2];
  //       setTrigger({
  //         curretNode: [pointBefore.path[0]],
  //         length: textLength,
  //         nextNode: nextPath,
  //         timeCheck: timeStampCheck,
  //       });
  //     }
  //   }
  // };

  const handleOnKeyDown = (event) => {
    const { selection } = editor;
    const lastNode = Editor.end(editor, []);
    if (selection && Range.isCollapsed(selection)) {
      // Get the current path and the last node's path
      // Check if the current path is the last node
      if (
        (lastNode.path[0] === selection.anchor.path[0] &&
          LiveTranscription &&
          event.key !== "ArrowUp") ||
        (lastNode.path[0] - 1 === selection.anchor.path[0] &&
          LiveTranscription &&
          interimResults &&
          !endTranscript &&
          event.key !== "ArrowUp")
      ) {
        event.preventDefault();
      }
    }
    if (event.altKey && event.key === "s") {
      updateState(data);
    } else if (
      (lastNode?.path[0] === selection?.anchor?.path[0]) !== true &&
      event.key === "Backspace"
    ) {
      const { selection } = editor;
      const { focus } = selection;
      const { offset } = focus;
      if (focus && offset === 0 && focus.path.length > 1) {
        const prevPath = focus.path.slice(0, focus.path.length - 1);
        const prevNode = Editor.node(editor, prevPath)[0];
        const addNode = Editor.node(editor, [prevPath - 1])[0];
        if (addNode?.children[0]?.text?.length === 0) {
          Transforms.setNodes(
            editor,
            {
              type: "timedText",
              mediaId: addNode?.mediaId + "," + prevNode?.mediaId,
              end: prevNode?.end,
            },
            { at: [prevPath] },
          );
        } else {
          Transforms.setNodes(
            editor,
            {
              type: "timedText",
              mediaId: addNode?.mediaId + "," + prevNode?.mediaId,
              end: prevNode?.end,
            },
            { at: [prevPath - 1] },
          );
        }
      } else {
        if (selection && Range.isExpanded(selection)) {
          const selectedNodes = Array.from(
            Editor.nodes(editor, { at: selection }),
          );

          // Extract mediaId values from selected nodes
          const mediaIds = selectedNodes
            .map(([node]) => node.mediaId)
            .filter((item) => item !== undefined);
          const joinedMediaIds = mediaIds.join(",");
          const [start] = Range.edges(selection);
          const [end] = Range.edges(selection, { reverse: true });
          const addNode = Editor.node(editor, [end.path[0]])[0];

          if (joinedMediaIds) {
            Transforms.setNodes(
              editor,
              { type: "timedText", mediaId: joinedMediaIds, end: addNode.end }, // Set the new joined mediaId
              // { match: (n) => Editor.hasPath(editor, n) },
              { at: [start.path[0]] },
            );
          }
          // const [start, end] = Range.edges(selection);
          // const range = { anchor: start, focus: end };
          // Transforms.delete(editor, { at: range });
          // const [node, nodePath] = Editor.node(editor, range.anchor.path);
          // const maxOffset = node.text.length; // Maximum offset for the node

          // // Validate the offset and set it to a valid value if out of bounds
          // const offset = Math.min(range.anchor.offset, maxOffset);
          // const adjustedSelection = {
          //   anchor: { path: nodePath, offset },
          //   focus: { path: nodePath, offset },
          // };
          // editor.selection = null;
          // setCursor({
          //   selection: adjustedSelection,
          // });
        }
      }
    } else if (
      (lastNode.path[0] === selection?.anchor?.path[0]) !== true &&
      event.key === "Delete" &&
      (lastNode.path[0] - 1 === selection?.anchor?.path[0]) !== true &&
      event.key === "Delete"
    ) {
      if (
        lastNode.path[0] - 1 === selection.anchor.path[0] ||
        lastNode.path[0] - 2 === selection.anchor.path[0]
      ) {
        const [node] = Editor.node(editor, selection);
        if (
          (node?.text.length === selection.anchor.offset &&
            LiveTranscription &&
            event.key !== "ArrowUp") ||
          (node?.text.length === selection.anchor.offset &&
            LiveTranscription &&
            interimResults &&
            !endTranscript &&
            event.key !== "ArrowUp")
        ) {
          event.preventDefault();
        }
      } else {
        const { selection } = editor;
        const { focus } = selection;
        const { offset } = focus;
        const prevPath = focus.path.slice(0, focus.path.length - 1);
        const prevNode = Editor.node(editor, prevPath)[0];
        if (prevNode.children[0].text.length === offset) {
          if (selection && Range.isExpanded(selection)) {
            const [start, end] = Range.edges(selection);
            const range = { anchor: start, focus: end };
            Transforms.delete(editor, { at: range });
            const [node, nodePath] = Editor.node(editor, range.anchor.path);
            const maxOffset = node.text.length; // Maximum offset for the node

            // Validate the offset and set it to a valid value if out of bounds
            const offset = Math.min(range.anchor.offset, maxOffset);
            const adjustedSelection = {
              anchor: { path: nodePath, offset },
              focus: { path: nodePath, offset },
            };
            editor.selection = null;
            setCursor({
              selection: adjustedSelection,
            });
          } else {
            const addNode = Editor.node(editor, [parseInt(prevPath) + 1])[0];
            const [start] = Range.edges(selection);
            const pointAfter = Editor.after(editor, start);
            const blockAfter = Editor.node(editor, [pointAfter.path[0]])[0];
            if (prevNode?.children[0]?.text?.length === 0) {
              Transforms.setNodes(
                editor,
                {
                  type: "timedText",
                  mediaId: prevNode?.mediaId + "," + addNode?.mediaId,
                  end: blockAfter?.end,
                },
                { at: [parseInt(prevPath) + 1] },
              );
            } else {
              Transforms.setNodes(
                editor,
                {
                  type: "timedText",
                  mediaId: prevNode?.mediaId + "," + addNode?.mediaId,
                  end: blockAfter?.end,
                },
                { at: [prevPath] },
              );
            }
          }
        } else {
          if (selection && Range.isExpanded(selection)) {
            const selectedNodes = Array.from(
              Editor.nodes(editor, { at: selection }),
            );

            // Extract mediaId values from selected nodes
            const mediaIds = selectedNodes
              .map(([node]) => node.mediaId)
              .filter((item) => item !== undefined);
            const joinedMediaIds = mediaIds.join(",");
            const [start] = Range.edges(selection);
            const [end] = Range.edges(selection, { reverse: true });
            const addNode = Editor.node(editor, [end.path[0]])[0];

            if (joinedMediaIds) {
              Transforms.setNodes(
                editor,
                {
                  type: "timedText",
                  mediaId: joinedMediaIds,
                  end: addNode.end,
                }, // Set the new joined mediaId
                // { match: (n) => Editor.hasPath(editor, n) },
                { at: [start.path[0]] },
              );
            }
            // const [start, end] = Range.edges(selection);
            // const range = { anchor: start, focus: end };
            // Transforms.delete(editor, { at: range });
            // const [node, nodePath] = Editor.node(editor, range.anchor.path);
            // const maxOffset = node.text.length; // Maximum offset for the node

            // // Validate the offset and set it to a valid value if out of bounds
            // const offset = Math.min(range.anchor.offset, maxOffset);
            // const adjustedSelection = {
            //   anchor: { path: nodePath, offset },
            //   focus: { path: nodePath, offset },
            // };
            // editor.selection = null;
            // setCursor({
            //   selection: adjustedSelection,
            // });
          }
        }
      }
    } else if (event.key === "Enter") {
      const { selection } = editor;
      if (selection && Range.isExpanded(selection)) {
        event.preventDefault();
        // const [start, end] = Range.edges(selection);
        // const range = { anchor: start, focus: end };
        // // Transforms.delete(editor, { at: range });
        // const [node, nodePath] = Editor.node(editor, range.anchor.path);
        // const maxOffset = node.text.length; // Maximum offset for the node

        // // Validate the offset and set it to a valid value if out of bounds
        // const offset = Math.min(range.anchor.offset, maxOffset);
        // const adjustedSelection = {
        //   anchor: { path: nodePath, offset },
        //   focus: { path: nodePath, offset },
        // };
        // editor.selection = null;
        // setCursor({
        //   selection: adjustedSelection,
        // });
      } else {
        const selected = Editor.last(editor, editor.selection);

        if (selected[0]?.color) {
          event.preventDefault();
        }
        const [start] = Range.edges(selection);

        const pointBefore = Editor.before(editor, start);
        const nodeBefore = Editor.node(editor, pointBefore)[0];
        const splitTxet = nodeBefore.text.split(" ");
        const textLength = splitTxet.length;
        const nextPath = [pointBefore.path[0] + 1];
        const timeStampCheck = [pointBefore.path[0] + 2];
        setTrigger({
          curretNode: [pointBefore.path[0]],
          length: textLength,
          nextNode: nextPath,
          timeCheck: timeStampCheck,
        });
      }
    } else if (event.altKey && event.key === "t") {
      const { selection } = editor;
      const [startNode] = Editor.path(editor, selection.anchor);
      const currentNode = Editor.node(editor, [startNode])[0];
      setState({
        state: true,
        element: currentNode,
        selection: editor.selection,
      });
    }
  };

  useEffect(() => {
    if (cursor?.selection) {
      editor.selection = cursor.selection;
      setCursor({});
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (trigger.curretNode) {
      const previousNode = value[trigger.curretNode];
      const previousTextLength = previousNode.children[0].text
        .split(" ")
        .filter((word) => word !== "");

      /* old code

      let timeInt =
        Math.ceil(
          (previousTextLength.length / trigger.length) *
            (previousNode.end - previousNode.start)
        ) + previousNode.start;
      let nextStop = value[trigger.timeCheck]
        ? value[trigger.timeCheck].start
        : "";

      if (nextStop && nextStop < timeInt) timeInt = nextStop - 1;
      Transforms.setNodes(
        editor,
        { type: "timedText", end: timeInt },
        { at: trigger.curretNode }
      );
      Transforms.setNodes(
        editor,
        { type: "timedText", start: timeInt },
        { at: trigger.nextNode }
      );

      */

      let timeInt =
        Math.ceil(
          (previousTextLength.length / trigger.length) *
            (previousNode.end - previousNode.start),
        ) + previousNode.start;
      let timeIntEnd = timeInt;
      let timeIntStart = timeInt;
      let nextStop = value[trigger.timeCheck]
        ? value[trigger.timeCheck].start
        : "";
      if (nextStop !== previousNode.start && nextStop < timeInt) {
        timeIntStart = nextStop - 1;
        timeIntEnd = nextStop - 1;
      } else if (nextStop === previousNode.start && nextStop < timeInt) {
        timeIntStart = previousNode.start;
        timeIntEnd = previousNode.end;
      }

      if (nextStop) {
        Transforms.setNodes(
          editor,
          { type: "timedText", end: timeIntEnd },
          { at: trigger.curretNode },
        );
        Transforms.setNodes(
          editor,
          { type: "timedText", start: timeIntStart },
          { at: trigger.nextNode },
        );
      }

      setTrigger({});
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleTimeClick = () => {
    editor.selection = null;
  };

  const highlightText = (currentTime) => {
    let boolean;
    if (currentTime) {
      for (let i = 0; i < value.length; i++) {
        let startTime = parseInt(value[i].start);
        let endTime = parseInt(value[i].end);
        if (startTime <= currentTime && endTime >= currentTime) {
          boolean = `${startTime} ${endTime}`;
          break;
        }
      }
      return boolean;
    }
  };

  /* Highlight 

  const handleSelectionChange = () => {
    const { selection } = editor;
    const captureText = window.getSelection().toString();
    if (selection && Range.isExpanded(selection)) {
      const [start, end] = Range.edges(selection);
      const [startNode] = Editor.path(editor, selection.anchor);
      const currentNode = Editor.node(editor, [startNode])[0];
      let highlightedText = [];
      let currentIndex = 0;
      const ranges = [{ start: start?.offset, end: end?.offset }];
      if (currentNode.children.length > 1) {
        const startPath = Editor.path(editor, start);
        const currentIndex = startPath[1];
        if (currentIndex !== -1) {
          const newArray =
            currentNode.children[currentIndex].text.split(captureText);
          const newChildren = newArray.reduce((acc, part, index) => {
            if (index !== newArray.length - 1) {
              acc.push({ text: part });
              acc.push({
                text: captureText,
                color: "rgba(89, 179, 48, 0.48)",
                name: name,
                role: role,
              });
            } else {
              acc.push({ text: part });
            }
            return acc;
          }, []);
          const sourceArrayCopy = [...currentNode.children];
          sourceArrayCopy[currentIndex] = newChildren;
          const updatedNode = {
            ...currentNode,
            children: sourceArrayCopy.flat(),
          };
          Transforms.removeNodes(editor, { updatedNode }, { at: [startNode] });
          Transforms.insertNodes(editor, updatedNode, { at: [startNode] });
          if (Range.isExpanded(selection)) {
            editor.selection = null;
          }
        }
      } else {
        ranges.forEach((range) => {
          const { start, end } = range;
          highlightedText.push({
            text: currentNode?.children[0].text.substring(currentIndex, start),
          });
          highlightedText.push({
            text: currentNode?.children[0].text.substring(start, end),
            color: "rgba(89, 179, 48, 0.48)",
            name: name,
            role: role,
          });
          currentIndex = end;
        });
        highlightedText.push({
          text: currentNode?.children[0].text.substring(currentIndex),
        });
        const updatedNode = { ...currentNode, children: highlightedText };
        Transforms.removeNodes(editor, { updatedNode }, { at: [startNode] });
        Transforms.insertNodes(editor, updatedNode, { at: [startNode] });
        if (Range.isExpanded(selection)) {
          editor.selection = null;
        }
      }
    } else {
      if (selection?.anchor && selection?.focus) {
        const [start, end] = Range.edges(selection);
        const range = { anchor: start, focus: end };
        const [node, nodePath] = Editor.node(editor, range.anchor.path);
        const maxOffset = node.text.length; // Maximum offset for the node

        // Validate the offset and set it to a valid value if out of bounds
        const offset = Math.min(range.anchor.offset, maxOffset);

        if (node.color && maxOffset === offset) {
          // Transforms.delete(editor, { at: range });

          const adjustedSelection = {
            anchor: { path: nodePath, offset },
            focus: { path: nodePath, offset },
          };
          editor.selection = adjustedSelection;
        }
      }
    }
  };

  useEffect(() => {
    if (captureText) {
      // Attach the event listener when the component mounts
      // document.addEventListener("mouseup", handleSelectionChange);
      return () => {
        // document.removeEventListener("mouseup", handleSelectionChange);
      };
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  */

  const handleSelectionChange = () => {
    const { selection } = editor;
    if (selection?.anchor && selection?.focus) {
      const [start, end] = Range.edges(selection);
      const range = { anchor: start, focus: end };
      const [node, nodePath] = Editor.node(editor, range.anchor.path);
      const maxOffset = node.text.length; // Maximum offset for the node
      // Validate the offset and set it to a valid value if out of bounds
      const offset = Math.min(range.anchor.offset, maxOffset);

      if (node.color && maxOffset === offset) {
        // Transforms.delete(editor, { at: range });
        const adjustedSelection = {
          anchor: { path: nodePath, offset },
          focus: { path: nodePath, offset },
        };
        editor.selection = adjustedSelection;
      }
    }
  };

  useEffect(() => {
    if (captureText) {
      // Attach the event listener when the component mounts
      document.addEventListener("mouseup", handleSelectionChange);
      return () => {
        document.removeEventListener("mouseup", handleSelectionChange);
      };
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // HoveringToolbar

  const toggleMark = (editor, format, colorCode) => {
    const isActive = isMarkActive(editor, format, colorCode);

    if (isActive) {
      Editor.removeMark(editor, format);
      Editor.removeMark(editor, "name");
      Editor.removeMark(editor, "role");
      editor.selection = null;
    } else {
      Editor.addMark(editor, format, colorCode);
      Editor.addMark(editor, "name", name);
      Editor.addMark(editor, "role", role);
    }
  };

  const isMarkActive = (editor, format, colorCode) => {
    const marks = Editor.marks(editor);
    return marks ? marks[format] === colorCode : false;
  };

  const decorate = useMemo(() => {
    return ([node, path]) => {
      const ranges = [];

      if (search && node.text) {
        const { text } = node;
        const parts = text.split(new RegExp(`(${search})`, "gi"));
        let offset = 0;

        parts.forEach((part, index) => {
          if (part.toLowerCase() === search.toLowerCase()) {
            ranges.push({
              anchor: { path, offset },
              focus: { path, offset: offset + part.length },
              color: "#AEE27E",
            });
          }
          offset += part.length;
        });
      }

      return ranges;
    };
  }, [search]);

  /**
   * @description Close modal
   */

  const closeModal = () => {
    setModal(false);
    setSearch("");
    setReplace("");
  };

  return (
    <>
      <TranscriptWrapper>
        <style scoped>
          {`
             .timecode[data-previous-timings*="${highlightText(currentTime)}"]{
                  color:  ${COLORS.BTN_GREEN};
              }
          `}
        </style>
        <TranscriptContent ref={containerRef}>
          {value.length !== 0 ? (
            <Slate
              editor={editor}
              value={value}
              onChange={(value) => {
                return setValue(value);
              }}
            >
              {isPlaying?.isPlaying ? null : <HoveringToolbar />}
              <Editable
                decorate={decorate}
                readOnly={false}
                autoFocus
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                onKeyDown={handleOnKeyDown}
                onMouseDown={handleTimeClick}
                onDOMBeforeInput={(event) => {
                  switch (event.inputType) {
                    case "formatHighlight":
                      event.preventDefault();
                      return toggleMark(editor, "rgba(89, 179, 48, 0.48)");
                    default:
                      break;
                  }
                }}
              />
            </Slate>
          ) : null}
        </TranscriptContent>
      </TranscriptWrapper>
      {state?.state ? (
        <UpdateSpeaker
          {...{ state, setState, speakersData, isEditable, editor, setCursor }}
        />
      ) : null}
      {modal ? (
        <div className={`modalContainer ${modal ? "show" : ""}`}>
          <div className="modal">
            <header className="modal_header">
              <h2 className="modal_header-title">Replace Text</h2>
              <button className="close">
                <CloseModal
                  src={require("../../../assets/images/closeModal.svg")}
                  onClick={closeModal}
                />
              </button>
            </header>
            <main className="modal_content">
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        width: 393,
                      }}
                      className={classes.tableHead}
                    >
                      Search word
                    </TableCell>
                    <TableCell
                      style={{
                        width: 393,
                      }}
                      className={classes.tableHead}
                    >
                      Replace with
                    </TableCell>
                    <TableCell className={classes.tableHead}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow className={"table-row"}>
                    <TableCell
                      component="th"
                      scope="row"
                      style={{ position: "relative" }}
                    >
                      <CustomInputField
                        variant="outlined"
                        className="input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search word"
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      style={{ position: "relative" }}
                    >
                      <CustomInputField
                        variant="outlined"
                        className="input"
                        value={replace}
                        onChange={(e) => setReplace(e.target.value)}
                        placeholder="Replace with"
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </main>
            <footer className="modal_footer">
              <button className="modal-close" onClick={closeModal}>
                Cancel
              </button>
              <button className="submit" onClick={handleReplaceAll}>
                Replace all
              </button>
              <button
                className="submit"
                onClick={() => replaceTextOneByOne(search, replace)}
              >
                Replace
              </button>
            </footer>
          </div>
        </div>
      ) : null}

      {commentComposer.open &&
        ReactDOM.createPortal(
          <CommentComposerCard
            style={{ top: commentComposer.top, left: commentComposer.left }}
          >
            <CommentComposerHeader>
              <CommentComposerAvatar>
                {getInitials(userObject?.name || "You")}
              </CommentComposerAvatar>
              <CommentComposerLabel>Add a comment</CommentComposerLabel>
              <CommentComposerCloseIcon
                onClick={closeCommentComposer}
                title="Close"
              >
                <Close style={{ fontSize: 15 }} />
              </CommentComposerCloseIcon>
            </CommentComposerHeader>
            <CommentComposerQuote>
              "
              {commentComposer.range
                ? Editor.string(editor, commentComposer.range)
                : ""}
              "
            </CommentComposerQuote>
            <CommentComposerTextarea
              autoFocus
              rows={3}
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") closeCommentComposer();
              }}
              placeholder="Type your suggestion or comment..."
            />
            <CommentComposerActions>
              <CommentCancelBtn onClick={closeCommentComposer}>
                Cancel
              </CommentCancelBtn>
              <CommentSubmitBtn
                onClick={submitComment}
                disabled={!commentDraft.trim()}
              >
                Comment
              </CommentSubmitBtn>
            </CommentComposerActions>
          </CommentComposerCard>,
          document.body,
        )}

      {activeComment &&
        ReactDOM.createPortal(
          <CommentThreadCard
            style={{ top: activeCommentPos.top, left: activeCommentPos.left }}
          >
            <CommentCloseBtn
              onClick={() => setActiveCommentId(null)}
              title="Close"
            >
              <Close style={{ fontSize: 16 }} />
            </CommentCloseBtn>
            <CommentThreadHeader>
              <CommentAuthorRow>
                <CommentAvatar>
                  {getInitials(activeComment.author)}
                </CommentAvatar>
                <CommentAuthorMeta>
                  <CommentAuthorName>{activeComment.author}</CommentAuthorName>
                  <CommentTimestamp>
                    {activeComment.createdAt
                      ? moment(activeComment.createdAt).format(
                          "DD-MM-YYYY, hh:mm A",
                        )
                      : ""}
                  </CommentTimestamp>
                </CommentAuthorMeta>
              </CommentAuthorRow>
              <CommentStatusBadge $status={activeComment.status}>
                {activeComment.status === "approved"
                  ? "Approved"
                  : activeComment.status === "rejected"
                    ? "Rejected"
                    : "Pending review"}
              </CommentStatusBadge>
            </CommentThreadHeader>
            <CommentFieldLabel>Original text</CommentFieldLabel>
            <CommentQuote>"{activeComment.quotedText}"</CommentQuote>
            <CommentFieldLabel>Suggestion / comment</CommentFieldLabel>
            <CommentBody>{activeComment.commentText}</CommentBody>
            {canApproveComments ? (
              <CommentThreadActions>
                <ApproveBtn
                  onClick={() => resolveComment(activeComment.id, "approved")}
                  disabled={activeComment.status === "approved"}
                >
                  <ThumbUp style={{ fontSize: 15, marginRight: 4 }} /> Approve
                </ApproveBtn>
                <RejectBtn
                  onClick={() => resolveComment(activeComment.id, "rejected")}
                  disabled={activeComment.status === "rejected"}
                >
                  <ThumbDown style={{ fontSize: 15, marginRight: 4 }} /> Reject
                </RejectBtn>
                <DeleteCommentBtn
                  onClick={() => deleteComment(activeComment.id)}
                  title="Delete comment"
                >
                  <Delete style={{ fontSize: 15 }} />
                </DeleteCommentBtn>
              </CommentThreadActions>
            ) : (
              <CommentViewerNote>
                Only users with editor access can approve or reject this
                comment.
              </CommentViewerNote>
            )}
          </CommentThreadCard>,
          document.body,
        )}
    </>
  );
};

export default Index;

/*

const Container = styled.div`
  display: flex;
`;
const DrawerContainer = styled.div`
  width: 100%;
`;

const ValueSpeaker = styled.span`
  width: auto;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 8px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  padding-right: 6px;
  @media ${theme.breakpoints.sm_down} {
    font-size: 14px;
  }
`;

const ValueTimesCode = styled.span`
  width: 80px;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 8px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  word-break: break-all;
  padding-right: 6px;
  &:hover {
    text-decoration: underline;
  }
  @media ${theme.breakpoints.sm_down} {
    font-size: 14px;
  }
`;

*/

export const ValueConfidence = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.TRANSCRIPT_GRAY};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
`;

/*

const ValueConfidence = styled.span`
  width: 68px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  line-height: 1.7;
  margin-right: 10px;
  margin-bottom: 8px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  word-break: break-all;
  padding-right: 6px;
  @media ${theme.breakpoints.sm_down} {
    font-size: 14px;
  }
`;

const ValueIds = styled.span`
  width: 50px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  line-height: 1.7;
  margin-bottom: 8px;
  color: ${COLORS.TRANSCRIPT_GRAY};
  word-break: break-all;
  padding-right: 5px;
  @media ${theme.breakpoints.sm_down} {
    font-size: 14px;
  }
`;

const CustomTextAreaOtter = styled.div`
  width: 85%;
  font-size: 16px;
  margin-left: 125px;
  margin-right: 25px;
  margin-bottom: 8px;
  line-height: 1.5;
  outline: none;
  border: none;
  resize: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background-color: ${COLORS.PRIMARY_WHITE};
  @media ${theme.breakpoints.sm_down} {
    margin-left: 30px;
    font-size: 14px;
  }
`;

*/

export const QuitContainer = styled.div`
  padding: 18px;
  margin-left: 117px;
  margin-right: 25px;
  border-radius: 7px;
  background-color: ${COLORS.LIGHT_BLUE};
  border: solid 1px ${COLORS.COLOR_DARK};
  margin-bottom: 12px;
  max-width: 80%;
`;

export const ProfileImage = styled.img`
  width: 20px;
  height: 25px;
  border-radius: 50%;
  object-fit: contain;
  margin-right: 4px;
  margin-left: 5px;
`;

export const AcceptButton = styled.button`
  border: 2px solid #00838c;
  background-color: white;
  outline: none;
  border-radius: 5px;
  border-width: 2.2px;
  border-style: solid;
  margin-left: 4px;
  height: 20px;
  cursor: pointer;
  color: #00838c;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

export const DeclineButton = styled.button`
  border: 2px solid #d91b1b;
  background-color: white;
  outline: none;
  border-radius: 5px;
  border-width: 2.2px;
  border-style: solid;
  margin-left: 4px;
  height: 20px;
  cursor: pointer;
  color: #d91b1b;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

export const ButtonWrapper = styled.div`
  display: flex;
`;

export const ActionButton = styled.button`
  border: none;
  background: none;
  outline: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  // margin-left: 5px;
`;

const CloseModal = styled.img`
  cursor: pointer;
  position: absolute;
  right: 20px;
`;

const FloatingHighlightBar = styled.div`
  position: absolute;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 2px;
  background: #0f172a;
  border-radius: 8px;
  padding: 4px 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  transform: translateX(-50%);
  animation: floatIn 0.15s ease-out;

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #0f172a;
  }

  @keyframes floatIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;

const FloatingToolbarBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const CommentedText = styled.span`
  cursor: pointer;
  border-radius: 3px;
  padding: 1px 2px;
  background-color: ${(p) =>
    p.$status === "approved"
      ? "#dcfce7"
      : p.$status === "rejected"
        ? "#fee2e2"
        : "#fef3c7"};
  border-bottom: 2px solid
    ${(p) =>
      p.$status === "approved"
        ? "#22c55e"
        : p.$status === "rejected"
          ? "#ef4444"
          : "#f59e0b"};
  transition: filter 0.15s;
  &:hover {
    filter: brightness(0.96);
  }
`;

const CommentComposerCard = styled.div`
  position: absolute;
  z-index: 1600;
  width: 300px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow:
    0 20px 40px rgba(15, 23, 42, 0.2),
    0 2px 8px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e9ef;
  padding: 14px;
  animation: commentPopIn 0.12s ease-out;

  @keyframes commentPopIn {
    from {
      opacity: 0;
      transform: translateY(-4px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const CommentComposerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const CommentComposerAvatar = styled.div`
  width: 26px;
  height: 26px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: linear-gradient(135deg, #00a3ad, #00696f);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 11px;
  letter-spacing: 0.02em;
`;

const CommentComposerLabel = styled.div`
  flex: 1;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
`;

const CommentComposerCloseIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 3px;
  border-radius: 50%;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;

const CommentComposerQuote = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  font-style: italic;
  color: #64748b;
  background: #f8fafc;
  border-left: 3px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  max-height: 60px;
  overflow-y: auto;
  word-break: break-word;
`;

const CommentComposerTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
  &:focus {
    border-color: #00838c;
    box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.1);
  }
`;

const CommentComposerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`;

const CommentCancelBtn = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  padding: 7px 12px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f1f5f9;
  }
`;

const CommentSubmitBtn = styled.button`
  background: ${COLORS.BTN_GREEN};
  color: #fff;
  border: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  padding: 7px 16px;
  border-radius: 7px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 131, 140, 0.25);
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  transition:
    background 0.15s,
    transform 0.1s;
  &:hover {
    background: ${(p) => (p.disabled ? COLORS.BTN_GREEN : "#006b73")};
  }
  &:active {
    transform: ${(p) => (p.disabled ? "none" : "scale(0.97)")};
  }
`;

const CommentThreadCard = styled.div`
  position: absolute;
  z-index: 1600;
  width: 300px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow:
    0 20px 40px rgba(15, 23, 42, 0.2),
    0 2px 8px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e9ef;
  padding: 16px;
  animation: commentPopIn 0.12s ease-out;
`;

const CommentThreadHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-right: 18px;
`;

const CommentAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CommentAvatar = styled.div`
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: linear-gradient(135deg, #00a3ad, #00696f);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 11px;
  letter-spacing: 0.02em;
`;

const CommentAuthorMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CommentAuthorName = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  color: ${COLORS.COLOR_DARK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CommentStatusBadge = styled.span`
  flex: 0 0 auto;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) =>
    p.$status === "approved"
      ? "#dcfce7"
      : p.$status === "rejected"
        ? "#fee2e2"
        : "#fef3c7"};
  color: ${(p) =>
    p.$status === "approved"
      ? "#15803d"
      : p.$status === "rejected"
        ? "#b91c1c"
        : "#b45309"};
`;

const CommentFieldLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 4px;
`;

const CommentQuote = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  font-style: italic;
  color: #64748b;
  background: #f8fafc;
  border-left: 3px solid #cbd5e1;
  border-radius: 4px;
  padding: 6px 10px;
  margin-bottom: 10px;
  max-height: 80px;
  overflow-y: auto;
  word-break: break-word;
`;

const CommentBody = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1.45;
  margin-bottom: 12px;
  word-break: break-word;
`;

const CommentTimestamp = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CommentThreadActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ApproveBtn = styled.button`
  display: flex;
  align-items: center;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
  border-radius: 7px;
  padding: 6px 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  &:hover {
    background: ${(p) => (p.disabled ? "#dcfce7" : "#bbf7d0")};
  }
`;

const RejectBtn = styled.button`
  display: flex;
  align-items: center;
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
  border-radius: 7px;
  padding: 6px 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  &:hover {
    background: ${(p) => (p.disabled ? "#fee2e2" : "#fecaca")};
  }
`;

const DeleteCommentBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #64748b;
  border-radius: 7px;
  padding: 6px 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #f1f5f9;
  }
`;

const CommentViewerNote = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`;

const CommentCloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  padding: 3px;
  border-radius: 50%;
  transition:
    background 0.15s,
    color 0.15s;
  &:hover {
    background: #f1f5f9;
    color: #475569;
  }
`;
